"use client"
import Head from "next/head"
import type { StaticImageData } from "next/image"
import Image, { getImageProps } from "next/image"
import { useState, useEffect, useRef } from "react"

interface LazyYouTubeProps {
  videoId: string
  customerName?: string
  title?: string
  maintainAspectRatio?: boolean
  thumbnailSrc?: string | StaticImageData
  thumbnailAlt?: string
  // Shows captions by default once the player loads.
  captions?: boolean
  // Preloads the poster at high priority on viewports matching this media
  // query: for an above-the-fold copy that CSS hides on other viewports. The
  // image itself stays lazy, so a hidden copy is never downloaded.
  preloadMedia?: string
}

const POSTER_SIZES = "(max-width: 768px) 100vw, 854px"

// maxresdefault (16:9) exists only for HD uploads; hqdefault always exists and
// its 4:3 letterbox bars are cropped away by object-cover in the 16:9 box.
const getPosterSrc = (videoId: string, useFallback: boolean) =>
  `https://i.ytimg.com/vi/${videoId}/${
    useFallback ? "hqdefault" : "maxresdefault"
  }.jpg`

function LazyYouTube({
  videoId,
  customerName,
  title = "YouTube video",
  maintainAspectRatio = false,
  thumbnailSrc,
  thumbnailAlt,
  captions = false,
  preloadMedia,
}: LazyYouTubeProps) {
  const [showIframe, setShowIframe] = useState(false)
  const [usePosterFallback, setUsePosterFallback] = useState(false)
  const [origin, setOrigin] = useState("")
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.origin)
    }
  }, [])

  // The play button unmounts on activation; hand keyboard focus to the player.
  useEffect(() => {
    if (showIframe) {
      iframeRef.current?.focus()
    }
  }, [showIframe])

  const buildSrc = (isTestimonial: boolean) => {
    const base = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&playsinline=1&rel=0&modestbranding=1&`

    const variant = isTestimonial
      ? "&controls=0&cc_load_policy=3"
      : "&controls=1&fs=1&iv_load_policy=3&color=white"

    const captionsPart = captions ? "&cc_load_policy=1" : ""
    const originPart = origin ? `&origin=${origin}` : ""

    return base + variant + captionsPart + originPart
  }
  const src = buildSrc(Boolean(customerName))
  const playerTitle = customerName ? `${customerName} testimonial` : title

  if (!showIframe) {
    const containerClassName = `relative block w-full cursor-pointer overflow-hidden rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue7 ${
      maintainAspectRatio ? "aspect-video" : "pb-[56.25%] md:h-[380px]"
    }`
    const playLabel = customerName
      ? `Play ${customerName} testimonial`
      : `Play video: ${title}`
    const poster = {
      src: getPosterSrc(videoId, usePosterFallback),
      alt: "",
      fill: true,
      sizes: POSTER_SIZES,
    }
    // The preload must request the same srcset candidate the <img> picks.
    const posterPreload =
      preloadMedia && !thumbnailSrc ? getImageProps(poster).props : undefined

    return (
      <>
        {posterPreload && (
          <Head>
            <link
              key={`youtube-poster-preload-${videoId}`}
              rel="preload"
              as="image"
              href={posterPreload.srcSet ? undefined : posterPreload.src}
              imageSrcSet={posterPreload.srcSet}
              imageSizes={posterPreload.sizes}
              media={preloadMedia}
              fetchPriority="high"
            />
          </Head>
        )}
        <button
          type="button"
          onClick={() => setShowIframe(true)}
          className={containerClassName}
          aria-label={playLabel}
        >
          {thumbnailSrc ? (
            <Image
              src={thumbnailSrc}
              alt={thumbnailAlt || title}
              fill
              className="rounded object-cover"
              sizes="(max-width: 768px) 100vw, 1289px"
              loading="lazy"
            />
          ) : (
            <Image
              {...poster}
              alt=""
              loading="lazy"
              className="rounded object-cover"
              onError={() => setUsePosterFallback(true)}
            />
          )}
          <span className="absolute inset-0 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="100"
              height="100"
              viewBox="0 0 48 48"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="#FF3D00"
                d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"
              ></path>
              <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
            </svg>
          </span>
        </button>
      </>
    )
  }

  return (
    <iframe
      ref={iframeRef}
      src={src}
      className="w-full aspect-video rounded"
      title={playerTitle}
      allowFullScreen
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; fullscreen;"
      key={videoId}
    />
  )
}

export default LazyYouTube
